/**
 * Status values for PromiseToPay. Mirrors the backend
 * (utils/promise-to-pay/promise-to-pay-status.js).
 *
 * Lifecycle:
 *   Requested : created by a collector, awaiting approval
 *   Arranged  : accepted by accounts. For a manual_debit promise this also means its
 *               KapCharge transaction has been raised — arranging is what raises it
 *   Processed : the money cleared
 *   NSF       : the payment was returned
 *   Broken    : the promise date passed with nothing received
 *   Cancelled : cancelled before completion
 *   Deleted   : soft-deleted
 */
export enum PromiseToPayStatus {
	Requested = 'Requested',
	Arranged = 'Arranged',
	Processed = 'Processed',
	NSF = 'NSF',
	Broken = 'Broken',
	Cancelled = 'Cancelled',
	Deleted = 'Deleted',
}

/** PTP catches up a missed instalment; Arrangement settles or pays down the balance. */
export enum PromiseToPayType {
	PTP = 'PTP',
	Arrangement = 'Arrangement',
}

export enum ArrangementType {
	PayOff = 'PayOff',
	PayOffExcludeNSF = 'PayOffExcludeNSF',
	PayDown = 'PayDown',
}

/**
 * ManualDebit ('manual_debit') is collected through KapCharge.
 * CustomerDeposit is money the customer sends themselves and never enters KapCharge.
 */
export enum PromiseToPayPaymentMethod {
	ManualDebit = 'manual_debit',
	CustomerDeposit = 'customer_deposit',
}

/**
 * Which NLS ledger an arranged manual_debit promise's collected amount posts against.
 * Mirrors the backend (utils/promise-to-pay/nls-transaction.js). Chosen in the arrange
 * dialog; meaningless for a customer_deposit, which never reaches NLS through this path.
 */
export enum NlsTransactionType {
	Payment = 'Payment',
	PrincipalReduction = 'PrincipalReduction',
	InterestReduction = 'InterestReduction',
}

export const NLS_TRANSACTION_TYPE_LABELS: Readonly<
	Record<NlsTransactionType, string>
> = {
	[NlsTransactionType.Payment]: 'Payment',
	[NlsTransactionType.PrincipalReduction]: 'Principal Reduction',
	[NlsTransactionType.InterestReduction]: 'Interest Reduction',
};

export const PROMISE_TO_PAY_STATUSES: PromiseToPayStatus[] = [
	PromiseToPayStatus.Requested,
	PromiseToPayStatus.Arranged,
	PromiseToPayStatus.Processed,
	PromiseToPayStatus.NSF,
	PromiseToPayStatus.Broken,
	PromiseToPayStatus.Cancelled,
	PromiseToPayStatus.Deleted,
];

/**
 * Per-status colours from the LMC palette (_variables.scss), matching how the
 * payment-processing feature resolves its own badge colours.
 */
export const PROMISE_TO_PAY_STATUS_COLORS: Readonly<
	Record<PromiseToPayStatus, string>
> = {
	[PromiseToPayStatus.Requested]: '#7986cb', // primary-300
	[PromiseToPayStatus.Arranged]: '#4351AF', // primary-500
	[PromiseToPayStatus.Processed]: '#4caf50', // success-500
	[PromiseToPayStatus.NSF]: '#d32f2f', // error-700
	[PromiseToPayStatus.Broken]: '#f57c00', // warning-700
	[PromiseToPayStatus.Cancelled]: '#9e9e9e', // neutral
	[PromiseToPayStatus.Deleted]: '#616161', // neutral-dark
};

/** Statuses a promise never leaves. Mirrors FINAL_STATUSES in the backend. */
export const PROMISE_TO_PAY_FINAL_STATUSES: PromiseToPayStatus[] = [
	PromiseToPayStatus.Processed,
	PromiseToPayStatus.NSF,
	PromiseToPayStatus.Broken,
	PromiseToPayStatus.Cancelled,
	PromiseToPayStatus.Deleted,
];

/**
 * Statuses whose fields may still be edited. Mirrors EDITABLE_STATUSES.
 *
 * Requested only: arranging raises the KapCharge transaction, so an edit afterwards would
 * leave the promise disagreeing with money that is already queued.
 */
export const PROMISE_TO_PAY_EDITABLE_STATUSES: PromiseToPayStatus[] = [
	PromiseToPayStatus.Requested,
];

/** Statuses a customer deposit may be set to from the edit dialog: any status. Mirrors DEPOSIT_EDIT_STATUSES. */
export const PROMISE_TO_PAY_DEPOSIT_EDIT_STATUSES: PromiseToPayStatus[] =
	PROMISE_TO_PAY_STATUSES;

/**
 * Statuses a promise may be cancelled from, and the same set it may be deleted from.
 * Mirrors CANCELLABLE_STATUSES / DELETABLE_STATUSES.
 *
 * Requested only: arranging raises a KapCharge transaction and an NLS payment, neither of
 * which cancelling the promise would undo. An arranged promise is unwound from Payment
 * Processing instead.
 */
export const PROMISE_TO_PAY_CANCELLABLE_STATUSES: PromiseToPayStatus[] = [
	PromiseToPayStatus.Requested,
];

export const PROMISE_TO_PAY_DELETABLE_STATUSES = PROMISE_TO_PAY_CANCELLABLE_STATUSES;

export const PROMISE_TO_PAY_TYPE_LABELS: Readonly<
	Record<PromiseToPayType, string>
> = {
	[PromiseToPayType.PTP]: 'Promise to Pay',
	[PromiseToPayType.Arrangement]: 'Arrangement',
};

export const ARRANGEMENT_TYPE_LABELS: Readonly<Record<ArrangementType, string>> =
	{
		[ArrangementType.PayOff]: 'Pay Off',
		[ArrangementType.PayOffExcludeNSF]: 'Pay Off (exclude NSF)',
		[ArrangementType.PayDown]: 'Pay Down',
	};

export const PAYMENT_METHOD_LABELS: Readonly<
	Record<PromiseToPayPaymentMethod, string>
> = {
	[PromiseToPayPaymentMethod.ManualDebit]: 'Manual Debit',
	[PromiseToPayPaymentMethod.CustomerDeposit]: 'Customer Deposit',
};

export namespace PromiseToPayStatus {
	/**
	 * Resolves the badge colour for a status from the shared palette. Bind it to the
	 * badge's `--status-color` custom property; the SCSS derives the pill's tinted
	 * background and text from that one value. Unknown statuses fall back to neutral.
	 */
	export function getColor(status: string | null | undefined): string {
		return (
			PROMISE_TO_PAY_STATUS_COLORS[status as PromiseToPayStatus] ??
			PROMISE_TO_PAY_STATUS_COLORS[PromiseToPayStatus.Cancelled]
		);
	}
}
